<script setup lang="ts">
import type { EditorialFashionShowModalProps } from './EditorialFashionShowModal.props'
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
const props = defineProps<EditorialFashionShowModalProps>()

const options: EmblaOptionsType = {
  slidesToScroll: 1,
  startIndex: props.selectedMediaIndex,
  align: 'center',
  loop: false,
}

const emblaApi = ref<any>(null)
const canScrollPrev = ref(false)
const canScrollNext = ref(false)

const onInit = (embla: Ref<EmblaCarouselType | undefined>) => {
  if (embla.value) {
    emblaApi.value = embla.value
    updateButtonStates()

    emblaApi.value.on('select', () => {
      updateButtonStates()
    })
  }
}

const scrollPrev = () => {
  if (emblaApi.value) {
    emblaApi.value.scrollPrev()
    updateButtonStates()
  }
}

const scrollNext = () => {
  console.log('scrollNext clicked', emblaApi.value)
  if (emblaApi.value) {
    emblaApi.value.scrollNext()
    updateButtonStates()
  }
}

const updateButtonStates = () => {
  if (!emblaApi.value) return

  if (!options.loop) {
    canScrollPrev.value = emblaApi.value.canScrollPrev()
    canScrollNext.value = emblaApi.value.canScrollNext()
  } else {
    canScrollPrev.value = true
    canScrollNext.value = true
  }
}

// Clean up event listener when component is unmounted
onBeforeUnmount(() => {
  if (emblaApi.value) {
    emblaApi.value.off('select', updateButtonStates)
  }
})
</script>

<template>
  <OrganismsModal id="galleryFashionShow" :is-editorial-look-grid="true">
    <template #body>
      <div class="relative h-full w-full">
        <button
          type="button"
          class="carousel-button carousel-button-prev hidden md:block"
          :disabled="!canScrollPrev"
          aria-label="Previous slide"
          @click.stop="scrollPrev"
        >
          <div class="arrow arrow--left"></div>
        </button>

        <UISliderBase :options="options" @init="onInit">
          <div
            v-for="(content, index) in cards"
            :key="`${'slide'}-${index}`"
            class="editorial-fashion-show-modal embla__slide"
          >
            <div class="flex h-[100dvh] flex-col justify-center gap-6">
              <div
                class="bg-background-secondary inline-flex h-[100dvh] flex-col items-center overflow-hidden"
              >
                <UIZoomableImage
                  v-if="content.media.media.type === 'image'"
                  :src="content.media.media.srcImage.secure_url!"
                  :alt="content.media.media.alt ?? ''"
                  :reset-zoom="0"
                  :is-gallery-slide-show="true"
                  class="positive-padding"
                />

                <UIFullScreenVideo
                  v-else-if="content.media.media.type === 'video'"
                  :src="content.media.media.srcVideo.secure_url"
                />
                <div
                  class="text-text-primary text-book-5 flex w-1/3 content-end justify-end px-2 py-8 text-end"
                >
                  <UILink
                    v-if="content.link && content.link[0]"
                    v-bind="content.link[0]"
                  />
                  <span v-else>
                    {{ `${index + 1}/${cards.length}` }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </UISliderBase>

        <button
          type="button"
          class="carousel-button carousel-button-next hidden md:block"
          :disabled="!canScrollNext"
          aria-label="Next slide"
          @click.stop="scrollNext"
        >
          <div class="arrow arrow--right"></div>
        </button>
      </div>
    </template>
  </OrganismsModal>
</template>

<style lang="scss">
.carousel-button {
  @apply absolute top-1/2 z-50 -translate-y-1/2;
  @apply text-text-primary transition-opacity duration-300;
  @apply hover:opacity-80 disabled:opacity-30;
  @apply h-12 w-12 cursor-pointer;
  background: transparent;
  border: none;
  pointer-events: auto;

  &.carousel-button-prev {
    @apply left-8;
  }

  &.carousel-button-next {
    @apply right-8;
  }

  &:focus {
    outline: none;
  }

  &:not(:disabled):hover {
    @apply opacity-80;
  }
}

.arrow {
  width: 12px;
  height: 12px;
  border-top: 2px solid currentColor;
  border-right: 2px solid currentColor;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-135deg);
  pointer-events: none;

  &--right {
    transform: translate(-50%, -50%) rotate(45deg);
  }
}

.custom-zoom-cursor-add {
  cursor:
    url('@design-system/icons/AddWBkg.svg') 24 24,
    auto;
}
</style>
